import { createQuestionFromConfiguration } from '../../../logbook/views/QuestionView';
import {
    Card,
    Exercise,
    LabelBody,
    LabelDisplay2,
    SecondaryButton,
    Spacer,
    Textarea,
} from '../../components';
import { IllustrationBulb, Module3S1Exercise2Illustration1 } from '../../icons';
import { ExerciseStatus } from '../../interfaces';
import { normalizeInput, useSyncedState } from '../../useSyncedState';
import { ExerciseData, ExerciseViewProps } from '../exercises';

const page1TextInputKey = 'exercise312Page1TextInputKey';

export const exerciseData312: ExerciseData = {
    ExerciseView: Module3S1Exercise2,
    dataKeys: [page1TextInputKey],
    questions: [
        createQuestionFromConfiguration({
            title: 'Et godt redskab',
            description:
                'Noter 3 ting, du har været taknemmelig for i dag. F.eks. at et æble smagte særligt godt, at man har sat pris på sin søster, at solen har skinnet.',
            dataKeys: [page1TextInputKey],
        }),
    ],
};

export function Module3S1Exercise2({
    exercise,
    onContinue,
}: ExerciseViewProps) {
    return (
        <Exercise title="Taknemmelighed">
            <Page />
        </Exercise>
    );

    function Page() {
        switch (exercise.CurrentPage) {
            case 1:
                return (
                    <Page1
                        textInput={exercise.Data[page1TextInputKey] ?? ''}
                        onContinue={(text) => {
                            onContinue({
                                status: ExerciseStatus.InProgress,
                                pageNumber: 2,
                                data: {
                                    [page1TextInputKey]: normalizeInput(text),
                                },
                            });
                        }}
                    />
                );
            case 2:
                return (
                    <Page2
                        onContinue={() => {
                            onContinue({
                                status: ExerciseStatus.Completed,
                                goToNext: false,
                            });
                        }}
                    />
                );
        }
    }
}

type Page1Props = {
    textInput: string;
    onContinue: (text: string) => void;
};

function Page1({ textInput, onContinue }: Page1Props) {
    const [text, setText] = useSyncedState(textInput);

    return (
        <>
            <LabelDisplay2 class="mb-10">3 gode ting</LabelDisplay2>
            <LabelBody class="mb-10">
                At være taknemmelig betyder, at man udtrykker at sætte pris på
                noget. Det kan være store ting i livet og helt små hverdagsting.
            </LabelBody>
            <LabelBody class="mb-10">
                Det at træne sin taknemmelighedsmuskel kan hjælpe hjernen med
                oftere at få øje på det der fungerer, fremfor mest at se på det
                der ikke gør.
            </LabelBody>
            <LabelBody class="mb-10">
                Som bonus kan taknemmelighed nedsætte ens stressniveau i
                kroppen, der ellers kan være med til at opretholde bekymringer
                og tankemylder.
            </LabelBody>
            <Module3S1Exercise2Illustration1 className="mx-auto my-30" />
            <Card class="mb-30">
                <LabelDisplay2 class="mb-10">Prøv det her</LabelDisplay2>
                <LabelBody>
                    Noter 3 ting, du har været taknemmelig for i dag. F.eks. at
                    et æble smagte særligt godt, at man har sat pris på sin
                    søster, at solen har skinnet.
                </LabelBody>
                <Textarea text={text} onChangeText={setText} />
                <LabelBody>
                    Dit svar bliver gemt i logbogen, når du fortsætter.
                </LabelBody>
            </Card>
            <Spacer />
            <SecondaryButton onClick={() => onContinue(text)}>
                Fortsæt
            </SecondaryButton>
        </>
    );
}

type PageProps = {
    onContinue: () => void;
};

function Page2({ onContinue }: PageProps) {
    return (
        <>
            <LabelDisplay2 class="mb-10">Et godt redskab</LabelDisplay2>
            <LabelBody>
                At træne sin taknemmelighedsmuskel kræver noget tid. Vi
                anbefaler, at du hver dag i en periode, skriver 3 ting ned, du
                på dagen har været taknemmelig for. Husk, at det gerne må være
                de helt små ting.
            </LabelBody>
            <IllustrationBulb className="mx-auto my-30" />
            <Spacer />
            <SecondaryButton onClick={onContinue}>
                Afslut øvelsen
            </SecondaryButton>
        </>
    );
}
